import styled from "styled-components";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";

import { TableContent, Th } from "@/components/Table";
import { ResponsiveTable, Table } from "@/components/Table";
import {
  ImageGenerationProjectResource,
  PaginatedResponse,
} from "@/types/resources";
import { SrOnly } from "@/components/Layout/Parts";
import ImageToolProjectsTableRow from "./ImageToolProjectsTableRow";
import { api } from "@/utils/api";
import InfiniteLoader from "@/components/InfiniteLoader";

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    &:first-child {
      width: calc(32% - 56px);
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 14%;
    }
    &:nth-child(3) {
      width: 20%;
    }
    &:nth-child(4) {
      width: 14%;
    }
    &:nth-child(5) {
      width: 20%;
    }
    &:last-child {
      width: 56px;
      position: relative;
    }
  }
`;

const ImageToolProjectsList = () => {
  const { type } = useParams();

  const navigate = useNavigate();

  const decideTypeFromRoute = () => {
    switch (type) {
      case "recents":
        return 0;
      case "automated":
        return 1;
    }
  };

  useEffect(() => {
    type !== "recents" &&
      type !== "automated" &&
      type !== "favorites" &&
      navigate("/404", {
        replace: true,
      });
  }, [type, navigate]);

  const fetchProjects = ({ pageParam = "/v1/image-generation-projects" }) => {
    return api.get<never, PaginatedResponse<ImageGenerationProjectResource>>(
      pageParam,
      {
        params: {
          "filter[automated]": decideTypeFromRoute(),
          ...(type === "favorites" && {
            "filter[isFavourite]": 1,
          }),
        },
      }
    );
  };

  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    data,
    isError,
    isLoading,
  } = useInfiniteQuery<PaginatedResponse<ImageGenerationProjectResource>>(
    ["image_generation_projects", { type }],
    fetchProjects,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  const imageProjects = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<ImageGenerationProjectResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <ResponsiveTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Project Name</Th>
              <Th>Date & Time</Th>
              {type !== "automated" && <Th>Creator</Th>}
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!imageProjects?.length}
            >
              {imageProjects?.map((project) => (
                <ImageToolProjectsTableRow project={project} key={project.id} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default ImageToolProjectsList;
